// client/src/components/MapView.js - OpenStreetMap Version - Refactored with Ant Design Spin/Alert
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Spin, Alert, Typography } from 'antd'; // Import Spin and Alert
// HINWEIS: Die mapService-Funktionen müssen eventuell leicht angepasst werden,
// um die Leaflet-Instanz (L) zurückzugeben oder direkt zu verwenden.
import { initializeMap, loadLeaflet, addShopMarkers } from '../services/mapService'; // Importiere addShopMarkers
// import './MapView.css'; // Remove CSS import

const { Text } = Typography;

const MapView = ({ shops }) => {
    const mapContainerDomRef = useRef(null); // Ref für das DOM Node
    const mapInstanceRef = useRef(null);   // Ref für die Leaflet Instanz
    const markerLayerRef = useRef(null);   // NEU: Ref für die Marker-FeatureGroup
    const [mapError, setMapError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [mapContainerReady, setMapContainerReady] = useState(false); // NEU: Zustand für Container Bereitschaft
    const [isInitializing, setIsInitializing] = useState(false); // Nur für den Init-Prozess
    const [mapInitialized, setMapInitialized] = useState(false); // NEU: Karte ist fertig initialisiert

    // Ref Callback: Wird aufgerufen, wenn das div gemounted/unmounted wird
    const mapContainerRefCallback = useCallback(node => {
        if (node !== null) {
            console.log("MapView: mapContainerRefCallback - Node attached.");
            mapContainerDomRef.current = node;
            setMapContainerReady(true); // Signal: Container ist im DOM
        } else {
            console.log("MapView: mapContainerRefCallback - Node detached.");
            mapContainerDomRef.current = null;
            setMapContainerReady(false);
            setMapInitialized(false); // Wichtig: Reset bei detach
            // Auch Map-Instanz und Layer entfernen/nullen beim detach
            if (mapInstanceRef.current) {
                try {
                    mapInstanceRef.current.remove(); // Karte komplett entfernen
                } catch (e) {
                    console.error("Error removing map instance:", e);
                }
                mapInstanceRef.current = null;
            }
            markerLayerRef.current = null; // Layer Ref auch zurücksetzen
        }
    }, []); // Keine Abhängigkeiten

    // Prüfen, ob das Gerät mobil ist
    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Hilfsfunktion zum Hinzufügen/Aktualisieren von Markern
    const updateMapMarkers = useCallback(async (map, currentShops) => {
        if (!map) return;
        console.log(`MapView: updateMapMarkers - Updating for ${currentShops?.length || 0} shops.`);
        try {
            await addShopMarkers(map, currentShops || []);
            console.log('MapView: updateMapMarkers - Markers updated successfully.');
        } catch (error) {
            console.error('MapView: updateMapMarkers - Error updating markers:', error);
            // setMapError('Fehler beim Aktualisieren der Marker.'); // Optional
        }
    }, []); // Keine Abhängigkeiten, nimmt map/shops als Argumente

    // Karte initialisieren
    useEffect(() => {
        // Nur wenn Container bereit ist, noch keine Instanz existiert und nicht schon initialisiert wird
        if (mapContainerReady && !mapInstanceRef.current && !isInitializing) {
            const initMap = async () => {
                console.log('MapView: initMap - Attempting initialization...');
                setIsInitializing(true);
                setMapInitialized(false); // Sicherstellen, dass es false ist
                setMapError(null);
                try {
                    await loadLeaflet();
                    const mapInstance = await initializeMap(mapContainerDomRef.current, {
                        zoom: isMobile ? 5 : 6,
                    });
                    mapInstanceRef.current = mapInstance;
                    console.log('MapView: initMap - Initialization successful.');

                    // NEU: Marker-Layer erstellen und zur Karte hinzufügen
                    const L = await loadLeaflet(); // Sicherstellen, dass L verfügbar ist
                    markerLayerRef.current = L.featureGroup().addTo(mapInstance);
                    console.log('MapView: initMap - Marker FeatureGroup created and added to map.');

                    setMapInitialized(true); // Signal: Karte ist jetzt initialisiert
                } catch (error) {
                    console.error('MapView: initMap - Error during initialization:', error);
                    setMapError('Fehler beim Initialisieren der Karte: ' + error.message);
                    mapInstanceRef.current = null;
                    setMapInitialized(false);
                } finally {
                    console.log('MapView: initMap - Setting isInitializing to false.');
                    setIsInitializing(false);
                }
            };
            initMap();
        }

        // KEIN Cleanup hier, da wir nicht wollen, dass die Karte bei isMobile-Änderung entfernt wird
        // Cleanup passiert nur im mapContainerRefCallback, wenn das Element entfernt wird.
    // Läuft nur, wenn Container bereit ist oder isMobile sich ändert
    }, [mapContainerReady, isMobile, isInitializing]);

    // Effekt zum Anpassen des Zooms
    useEffect(() => {
        if (mapInitialized && mapInstanceRef.current) {
            const targetZoom = isMobile ? 5 : 6;
            console.log(`MapView: Adjusting zoom for isMobile=${isMobile}. Target: ${targetZoom}`);
            try {
                mapInstanceRef.current.setZoom(targetZoom);
            } catch (e) {
                console.error("MapView: Error setting zoom:", e);
            }
        }
    // Reagiert auf isMobile und mapInitialized
    }, [isMobile, mapInitialized]);

    // Effekt zum Aktualisieren der Marker
    useEffect(() => {
        // Nur wenn Karte initialisiert ist
        if (mapInitialized && mapInstanceRef.current) {
            console.log(`MapView: Shops changed or map initialized, updating markers for ${shops?.length || 0} shops.`);
            const update = async () => {
                 // Optional: Zeige kurz einen Indikator während Marker geladen werden?
                 // setIsProcessingMarkers(true);
                 try {
                     // await addShopMarkers(mapInstanceRef.current, shops || []);
                     // NEU: Übergebe markerLayerRef.current an addShopMarkers
                     await addShopMarkers(mapInstanceRef.current, shops || [], markerLayerRef.current);
                     console.log('MapView: Markers updated successfully via mapService.');
                 } catch (error) {
                     console.error('MapView: Error updating markers:', error);
                 } finally {
                    // setIsProcessingMarkers(false);
                 }
            };
            update();
        } else {
             console.log("MapView: Skipping marker update, map not initialized yet.");
        }
    // Läuft, wenn shops sich ändern oder die Karte initialisiert wurde
    }, [shops, mapInitialized]);

    // --- Render-Logik ---
    const mapContainerStyle = { width: '100%', height: '400px', position: 'relative' };
    const overlayStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 10, // Ensure overlay is above map tiles but below popups
        textAlign: 'center',
        padding: '20px'
    };
    const spinnerOverlayStyle = {
        ...overlayStyle, // Reuse common styles
        zIndex: 20, // Higher zIndex to be on top
        backgroundColor: 'rgba(255, 255, 255, 0.7)', // Slightly more opaque
    };

    // Fehler zuerst anzeigen
    if (mapError) {
        return (
            <div style={mapContainerStyle}>
                <Alert message="Kartenfehler" description={mapError} type="error" showIcon />
                <div ref={mapContainerRefCallback} style={{ display: 'none' }}></div>
            </div>
        );
    }

    // Haupt-Render: Immer den Container rendern
    return (
        <div style={mapContainerStyle}>
            {/* Actual map container mit Ref Callback */}
            <div ref={mapContainerRefCallback} style={{ width: '100%', height: '100%' }} />

            {/* Loading Spinner nur während der Initialisierung */}
            {isInitializing && (
                 <div style={spinnerOverlayStyle}>
                    <Spin size="large" tip="Karte wird initialisiert..." />
                 </div>
            )}

            {/* No Shops Overlay (nur wenn nicht initialisiert wird und keine Shops da sind) */}
            {!isInitializing && !mapError && (!shops || shops.length === 0) && (
                 <div style={overlayStyle}>
                     <Text strong style={{fontSize: '1.2em', marginBottom: '10px'}}>Keine Shops gefunden</Text>
                     <Text>Für den aktuellen Standort oder Filter wurden keine Shops gefunden.</Text>
                     <Text><Link to="/register-shop">Registrieren Sie Ihren Shop!</Link></Text>
                 </div>
            )}
        </div>
    );
};

// Nur ein Default Export
export default MapView;